import { useSpringValue } from '@react-spring/web';
import { DragState, useDrag, UserDragConfig } from '@use-gesture/react';
import { useEffect, useState } from 'react';

import { getSelectionText } from '../lib/getSelection';
import { isInInputElement } from '../lib/isInElement';

function checkDisabledElement(axis, target, root) {
    if (target === root || target === null) {
        return false;
    }
    const classList = target.classList || null;
    if (
        isInInputElement(target) ||
        (classList !== null && target.classList.contains('drag-disabled')) ||
        (classList !== null && target.classList.contains(`drag-${axis}-disabled`))
    ) {
        return true;
    }
    return checkDisabledElement(axis, target.parentNode, root);
}

function defaultCheckDisabled(axis, target, root) {
    return (
        checkDisabledElement(axis, target, root) ||
        getSelectionText().length > 0 ||
        isInInputElement(document.activeElement || null)
    );
}

interface UseDragProgressOptions {
    progress?: number;
    onTap?: ((gestureState: DragState) => void) | null;
    disabled?: boolean;
    dragDisabled?: boolean;
    computeProgress?: (gestureState: DragState) => number | null;
    onProgress?: ((progress: number, gestureState: DragState) => void) | null;
    onStart?: (() => void) | null;
    onComplete?: (({ value }: { value: number }) => void) | null;
    springParams?: Record<string, unknown> | null;
    checkDisabled?: (axis: string, target: EventTarget | null, root: EventTarget | null) => boolean;
    threshold?: number;
    draggingTimeout?: number;
    dragOptions?: UserDragConfig;
}

const defaultDragOptions: UserDragConfig = {
    filterTaps: false,
    axis: 'x',
    pointer: {
        capture: false,
    },
};

function useDragProgress({
    progress: wantedProgress = 0,
    onTap = null,
    disabled = false,
    dragDisabled = false,
    computeProgress = null,
    onProgress = null,
    onStart: onTransitionStart = null,
    onComplete: onTransitionComplete = null,
    springParams = undefined,
    checkDisabled = defaultCheckDisabled,
    threshold = 0,
    draggingTimeout = 5000,
    dragOptions = defaultDragOptions,
}: UseDragProgressOptions = {}) {
    'use no memo';

    const [progressState, setProgressState] = useState(wantedProgress);
    const [lastCancel, setLastCancel] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [direction, setDirection] = useState(0);
    const [transitioned, setTransitioned] = useState(true);
    const progress = useSpringValue(wantedProgress, {
        immediate: dragging || disabled,
        ...springParams,
    });
    const startDragging = () => {
        setDragging(true);
        setTransitioned(false);
    };
    const stopDragging = () => {
        setDragging(false);
    };
    const onStart = (e) => {
        setTransitioned(false);
        if (onTransitionStart !== null) {
            onTransitionStart();
        }
    };
    const onRest = (e) => {
        setDirection(0);
        setTransitioned(true);
        if (onTransitionComplete !== null) {
            onTransitionComplete(e);
        }
    };
    const onDrag = (gestureState) => {
        const {
            active,
            tap,
            canceled,
            cancel: cancelGesture,
            event,
            axis,
            first,
            last,
        } = gestureState;
        const startedInDisabledElement =
            first && checkDisabled(axis, event.target, event.currentTarget);
        if (startedInDisabledElement) {
            // Only the first event is checked: cancel the gesture so the next ones don't drag.
            cancelGesture();
        }
        if (disabled || dragDisabled || startedInDisabledElement) {
            setTransitioned(true);
            if (onTransitionComplete !== null) {
                onTransitionComplete({ value: progress.get() });
            }
            stopDragging();
            return;
        }

        if (tap && onTap !== null) {
            onTap(gestureState);
        }

        const newProgress = computeProgress(gestureState);

        const newDirection = newProgress >= wantedProgress ? 1 : -1;
        if (newDirection !== direction) {
            setDirection(newDirection);
        }

        if (active && first) {
            startDragging();
            setLastCancel({
                cancel: cancelGesture,
            });
        } else if (!active || canceled) {
            stopDragging();
            setLastCancel(null);
        }
        progress.start(newProgress, {
            immediate: active && !canceled,
            onStart: first ? onStart : undefined,
            onRest: last ? onRest : undefined,
            ...springParams,
        });
        if (onProgress !== null) {
            onProgress(newProgress, gestureState);
        }
    };

    const bind = useDrag(onDrag, {
        threshold,
        ...dragOptions,
    });

    if (!dragging && wantedProgress !== progressState) {
        setProgressState(wantedProgress);
        setDirection(wantedProgress > progressState ? 1 : -1);
        setTransitioned(false);
        progress.start(wantedProgress, {
            immediate: false,
            onStart: onStart,
            onRest: onRest,
            ...springParams,
        });
    }

    const finalDisabled = disabled || dragDisabled;

    if (finalDisabled && dragging) {
        stopDragging();
    }

    if (!dragging && lastCancel !== null) {
        lastCancel.cancel();
        setLastCancel(null);
    }

    useEffect(() => {
        if (!dragging) {
            return () => {};
        }
        const timeout = setTimeout(() => {
            stopDragging();
        }, draggingTimeout);
        return () => clearTimeout(timeout);
    }, [draggingTimeout, dragging]);

    return {
        direction,
        transitioned: transitioned && !dragging && wantedProgress === progressState,
        bind,
        dragging,
        progress,
    };
}

export default useDragProgress;
